import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  alpha,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PublicPageShell from '../components/PublicPageShell';

const PRIMARY = '#0087c1';

const PLANS = [
  {
    name: 'Starter',
    price: '£40',
    period: '/month',
    description: 'For small companies and teams with Struxor.',
    bullets: ['Up to 2 users', '100 GB storage', '300 monthly AI requests', 'Unlimited projects', 'Full app functionality', 'Onboarding & training'],
  },
  {
    name: 'Team',
    price: '£200',
    period: '/month',
    description: 'For growing construction teams who need collaboration and shared project visibility.',
    bullets: ['Up to 8 users', '1 TB storage', '1,500 monthly AI requests', 'Unlimited projects', 'Full app functionality', 'Onboarding & training'],
  },
  {
    name: 'Organisation',
    price: '£800',
    period: '/month',
    description: 'For larger companies managing multiple crews and projects across the business.',
    bullets: ['Up to 35 users', '3 TB storage', '5,000 monthly AI requests', 'Unlimited projects', 'Full app functionality', 'Onboarding & training'],
  },
  {
    name: 'Enterprise',
    price: 'Competitive',
    period: '',
    description: 'For organisations that need custom user counts and dedicated support.',
    bullets: ['Custom users & storage', 'Dedicated account manager', 'Custom integrations', 'Unlimited projects', 'Full app functionality', 'Onboarding & training'],
  },
];

const COMPARISON_ROWS = [
  { feature: 'AI Assistant', vals: ['full', 'full', 'no', 'partial', 'no', 'no'], labels: ['Actions — quotes, forms, emails', 'Copilot (Helix)', '—', 'Search only', '—', '—'] },
  { feature: 'Project Lifecycle', vals: ['full', 'full', 'partial', 'partial', 'full', 'full'], labels: ['Quote → Order → Completion', 'Full lifecycle', 'Task-focused', 'Defect-focused', 'Estimate → Job', 'Estimate → Invoice'] },
  { feature: 'Digital Forms', vals: ['full', 'full', 'full', 'full', 'no', 'partial'], labels: ['Templates, signatures, PDF', '✓', 'Custom forms', 'Custom templates', '—', 'Daily logs only'] },
  { feature: 'Timesheets', vals: ['full', 'partial', 'no', 'no', 'no', 'full'], labels: ['Clock in/out & approvals', 'Add-on', '—', '—', '—', '✓'] },
  { feature: 'Purchase Orders', vals: ['full', 'full', 'no', 'no', 'full', 'full'], labels: ['Project & standalone', '✓', '—', '—', '✓', '✓'] },
  { feature: 'RAMS & Safety', vals: ['full', 'partial', 'partial', 'full', 'no', 'no'], labels: ['Signatures & sign-off', 'Add-on', 'Via tasks', 'Defect tracking', '—', '—'] },
  { feature: 'Plan Markup', vals: ['full', 'full', 'full', 'full', 'no', 'partial'], labels: ['Pin markup + activities', '✓', 'Core strength', 'Core strength', '—', 'Basic'] },
  { feature: 'Offline Mode', vals: ['full', 'full', 'full', 'full', 'no', 'no'], labels: ['Full offline + sync', '✓', '✓', '✓', '—', '—'] },
  { feature: 'Mobile App', vals: ['full', 'full', 'full', 'full', 'partial', 'partial'], labels: ['Native iOS & Android', '✓', '✓', '✓', 'Limited', 'Limited'] },
  { feature: 'Cost (8 users/mo)', vals: ['full', 'partial', 'no', 'no', 'partial', 'full'], labels: ['£200 flat', 'Custom quote', '£425–£593', '£640–£952', 'Custom', '~£135'] },
];

const FAQS = [
  { q: 'Is there a free trial?', a: 'Yes. Every plan includes a 14-day free trial with unlimited users and projects — no credit card required. Start building your first project within minutes.' },
  { q: 'Are plan prices inclusive of VAT?', a: 'No. Prices exclude VAT. Any VAT charged will be shown separately before payment and on your invoice.' },
  { q: 'Can I change my plan later?', a: 'Absolutely. You can upgrade or downgrade your plan at any time from your account settings. Changes take effect at the start of your next billing period.' },
  { q: 'Do you offer annual billing?', a: 'Not currently — all plans are billed monthly with no long-term contracts. You can cancel at any time.' },
  { q: 'Can I add more users to my plan?', a: 'Each plan has a user limit (1, 8, or 35). If you need more users, upgrade to the next plan or contact us for Enterprise pricing.' },
  { q: 'How does Struxor compare to Procore on price?', a: 'Procore requires a custom enterprise quote with an annual contract. Struxor starts at £40/month with transparent, flat pricing and no long-term commitment.' },
];

export default function Pricing() {
  const theme = useTheme();

  return (
    <PublicPageShell>
      <Helmet>
        <title>Pricing — Plans from £40/month | Struxor Construction Software</title>
        <meta
          name="description"
          content="Struxor pricing starts at £40/month for solo contractors. Team, Organisation, and Enterprise plans available. 14-day free trial, no credit card required."
        />
        <link rel="canonical" href="https://www.struxorapp.com/pricing" />
        <meta property="og:title" content="Pricing — Plans from £40/month | Struxor Construction Software" />
        <meta property="og:description" content="Struxor pricing starts at £40/month for solo contractors. Team, Organisation, and Enterprise plans available. 14-day free trial, no credit card required." />
        <meta property="og:url" content="https://www.struxorapp.com/pricing" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Struxor Pricing',
          description: 'Transparent, flat-rate pricing for Struxor construction software. Plans from £40/month. 14-day free trial, no credit card required.',
          url: 'https://www.struxorapp.com/pricing',
          isPartOf: { '@type': 'WebSite', name: 'Struxor', url: 'https://www.struxorapp.com' },
        })}</script>
      </Helmet>

      {/* Hero */}
      <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #005a8c 100%)`, color: '#fff', py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h1" fontWeight={800} sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, mb: 2 }}>
            Pricing that scales with your team
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, maxWidth: 600, mx: 'auto', fontSize: { xs: '1rem', md: '1.15rem' } }}>
            Flat monthly pricing. No per-user fees. No annual contracts. Every plan starts with a 14-day free trial.
          </Typography>
        </Container>
      </Box>

      {/* Plans */}
      <Container sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={3} alignItems="stretch">
          {PLANS.map((plan) => (
            <Grid key={plan.name} item xs={12} sm={6} md={3}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 4,
                  border: `1px solid ${theme.palette.divider}`,
                  transition: 'transform 200ms ease, box-shadow 200ms ease',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 10px 24px rgba(0,0,0,0.08)' },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: { xs: 2.5, md: 3 }, display: 'flex', flexDirection: 'column' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="h6" fontWeight={700}>{plan.name}</Typography>
                  </Stack>
                  <Typography variant="h3" fontWeight={800} sx={{ mb: 0.5, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                    {plan.price}
                    {plan.period && (
                      <Typography component="span" variant="subtitle2" color="text.secondary">
                        {plan.period}
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.5 }}>
                    {plan.description}
                  </Typography>
                  <Stack spacing={1.2} sx={{ flexGrow: 1, mb: 3 }}>
                    {plan.bullets.map((b) => (
                      <Stack direction="row" spacing={1} alignItems="center" key={b}>
                        <CheckCircleRoundedIcon sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />
                        <Typography variant="body2">{b}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                  <Button
                    component={RouterLink}
                    to="/register"
                    variant="outlined"
                    fullWidth
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      borderColor: PRIMARY,
                      color: PRIMARY,
                    }}
                  >
                    {plan.name === 'Enterprise' ? 'Contact Us' : 'Start Free Trial'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 3 }}>
          * Trial limited to 14 days, 100 GB storage, 300 AI requests, unlimited users and projects. No credit card required.
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1.5, maxWidth: 720, mx: 'auto', lineHeight: 1.7 }}>
          Prices exclude VAT. Any VAT charged will be shown separately before payment and on your invoice.
        </Typography>
      </Container>

      {/* Comparison table */}
      <Box sx={{ bgcolor: theme.palette.mode === 'light' ? '#f8fafc' : alpha(theme.palette.background.paper, 0.5), borderTop: `1px solid ${theme.palette.divider}`, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container sx={{ py: { xs: 6, md: 10 } }}>
          <Typography variant="h4" component="h2" align="center" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
            How Struxor compares
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 5, maxWidth: 650, mx: 'auto' }}>
            All-in-one construction software with AI — see how Struxor stacks up against Procore, Fieldwire, PlanRadar, Buildxact, and Knowify.
          </Typography>

          <Box sx={{
            overflowX: 'auto',
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-thumb': { borderRadius: 3, bgcolor: alpha(theme.palette.text.primary, 0.15) },
          }}>
            <Box sx={{ minWidth: 700, border: `1px solid ${theme.palette.divider}`, borderRadius: 4, overflow: 'hidden', bgcolor: theme.palette.background.paper }}>
              {/* Header */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr repeat(6, minmax(90px, 1fr))', bgcolor: theme.palette.mode === 'light' ? '#f1f5f9' : alpha(theme.palette.background.paper, 0.8), borderBottom: `1px solid ${theme.palette.divider}` }}>
                {['Feature', 'Struxor', 'Procore', 'Fieldwire', 'PlanRadar', 'Buildxact', 'Knowify'].map((h, i) => (
                  <Box key={h} sx={{ px: 2, py: 1.5, fontWeight: 700, fontSize: '0.85rem', color: i === 1 ? 'primary.main' : 'text.primary', textAlign: i === 0 ? 'left' : 'center', borderRight: i < 6 ? `1px solid ${theme.palette.divider}` : 'none' }}>
                    {h}
                  </Box>
                ))}
              </Box>
              {/* Rows */}
              {COMPARISON_ROWS.map((row, idx) => (
                <Box key={row.feature} sx={{ display: 'grid', gridTemplateColumns: '1fr repeat(6, minmax(90px, 1fr))', borderBottom: idx < COMPARISON_ROWS.length - 1 ? `1px solid ${theme.palette.divider}` : 'none', '&:hover': { bgcolor: alpha(PRIMARY, 0.03) } }}>
                  <Box sx={{ px: 2, py: 1.5, fontWeight: 600, fontSize: '0.85rem', borderRight: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center' }}>{row.feature}</Box>
                  {row.vals.map((v, i) => (
                    <Box key={i} sx={{ px: 1, py: 1.5, textAlign: 'center', borderRight: i < 5 ? `1px solid ${theme.palette.divider}` : 'none', bgcolor: i === 0 ? alpha(PRIMARY, 0.04) : 'transparent', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      {v === 'full' && <CheckCircleRoundedIcon sx={{ fontSize: 18, color: 'success.main' }} />}
                      {v === 'partial' && <RemoveRoundedIcon sx={{ fontSize: 18, color: 'warning.main' }} />}
                      {v === 'no' && <CloseRoundedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />}
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{row.labels[i]}</Typography>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 3, fontSize: '0.8rem' }}>
            Comparison based on publicly available information as of 2026. Features may vary by plan.
          </Typography>
        </Container>
      </Box>

      {/* FAQ */}
      <Container sx={{ py: { xs: 6, md: 10 } }}>
        <Typography variant="h4" component="h2" align="center" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
          Pricing FAQ
        </Typography>
        <Box sx={{ maxWidth: 750, mx: 'auto', mt: 4 }}>
          {FAQS.map((faq, idx) => (
            <Accordion
              key={idx}
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '12px !important',
                mb: 1.5,
                '&:before': { display: 'none' },
                '&.Mui-expanded': { mb: 1.5 },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ fontWeight: 600 }}>{faq.q}</AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Container>

      {/* CTA */}
      <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #005a8c 100%)`, py: { xs: 6, md: 8 }, textAlign: 'center', color: '#fff' }}>
        <Container maxWidth="sm">
          <Typography variant="h4" fontWeight={700} mb={2} sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Start managing projects like a pro
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
            14-day free trial. No credit card. No long-term contract.
          </Typography>
          <Button
            component={RouterLink}
            to="/register"
            variant="contained"
            size="large"
            sx={{ background: '#fff', color: PRIMARY, fontWeight: 700, textTransform: 'none', borderRadius: 2, px: 5, '&:hover': { background: alpha('#fff', 0.9) } }}
          >
            Start Free Trial
          </Button>
        </Container>
      </Box>
    </PublicPageShell>
  );
}
